import HttpService from '@/api/HttpService';
import {
  GET_NOTES_LIST_FAILURE,
  GET_NOTES_LIST_REQUEST,
  GET_NOTES_LIST_SUCCESS,
  PURCHASE_NOTE_REQUEST,
  PURCHASE_NOTE_FAILURE,
  PURCHASE_NOTE_SUCCESS,
} from '../mutation-types';

export default {
  namespaced: true,
  state: {
    purchasingNote: false,
    gettingNotesList: false,
  },
  mutations: {
    [GET_NOTES_LIST_FAILURE](state) {
      state.gettingNotesList = false;
    },
    [GET_NOTES_LIST_REQUEST](state) {
      state.gettingNotesList = true;
    },
    [GET_NOTES_LIST_SUCCESS](state) {
      state.gettingNotesList = false;
    },
    [PURCHASE_NOTE_FAILURE](state) {
      state.purchasingNote = false;
    },
    [PURCHASE_NOTE_REQUEST](state) {
      state.purchasingNote = true;
    },
    [PURCHASE_NOTE_SUCCESS](state) {
      state.purchasingNote = false;
    },
  },
  actions: {
    getInterestYearRates() {
      return HttpService.getV2('notes/rates');
    },
    getRedeemNote({ commit }, noteId) {
      return HttpService.get(`/notes/${noteId}/redeem`)
        .then(response => response)
        .catch((err) => {
          throw err;
        });
    },
    getAccountNoteRedeem({ commit }, { accountId, noteId }) {
      return HttpService.getV2(`/accounts/${accountId}/notes/${noteId}/redemptions`)
        .then(response => response)
        .catch((err) => {
          throw err;
        });
    },
    validateRedemptionFunds({ commit }, noteId) {
      return HttpService.getV2(`/notes/${noteId}/redeem-validate-funds`)
        .then(response => response)
        .catch((err) => {
          throw err;
        });
    },
    requestNoteRedemption({ commit }, { accountId, noteId, payload }) {
      return HttpService.postV2(`/accounts/${accountId}/notes/${noteId}/redemptions`, payload)
        .then(() => true)
        .catch(error => Promise.reject(error));
    },
    approveNoteRedemption({ commit }, { noteId, redeemRequest }) {
      return HttpService.postV2(`/notes/${noteId}/redemption/approval`, redeemRequest)
        .then(() => true)
        .catch(error => Promise.reject(error));
    },
    cancelNoteRedemption({ commit }, { accountId, noteId, payload }) {
      return HttpService.deleteV2(`/accounts/${accountId}/notes/${noteId}/redemptions`, payload)
        .then(() => true)
        .catch(error => Promise.reject(error));
    },
    // Bulk "redeem/approve all open notes". The preview drives the modal: the
    // service fee is charged once for the batch, to whichever note is large
    // enough to absorb it, so the server decides where it lands and re-prices
    // whenever the admin edits it rather than the modal computing it.
    previewBulkRedemption({ commit }, { accountId, serviceFee = 0 }) {
      return HttpService.getV2(
        `/accounts/${accountId}/notes/bulk-redemptions/preview?serviceFee=${serviceFee}`,
      )
        .then(response => response)
        .catch(error => Promise.reject(error));
    },
    createBulkRedemption({ commit }, { accountId, payload }) {
      return HttpService.postV2(`/accounts/${accountId}/notes/bulk-redemptions`, payload)
        .then(response => response)
        .catch(error => Promise.reject(error));
    },
    getBulkRedemption({ commit }, { accountId, batchId }) {
      return HttpService.getV2(`/accounts/${accountId}/notes/bulk-redemptions/${batchId}`)
        .then(response => response)
        .catch(error => Promise.reject(error));
    },
    approveBulkRedemption({ commit }, { accountId, batchId, payload }) {
      return HttpService.postV2(
        `/accounts/${accountId}/notes/bulk-redemptions/${batchId}/approval`,
        payload,
      )
        .then(response => response)
        .catch(error => Promise.reject(error));
    },
    cancelBulkRedemption({ commit }, { accountId, batchId, payload }) {
      return HttpService.deleteV2(
        `/accounts/${accountId}/notes/bulk-redemptions/${batchId}`,
        payload,
      )
        .then(response => response)
        .catch(error => Promise.reject(error));
    },
    // Bulk "transfer every note to another account". The preview reports which
    // notes can move and what it would cost the investor in cleared upcoming
    // transaction actions; the destination is optional because eligibility
    // depends on the source account alone.
    previewBulkNoteTransfer({ commit }, { accountId, toAccountId, includeClosedNotes }) {
      // Built up rather than declared: URLSearchParams would serialise an absent
      // value as the string "undefined" and the endpoint would reject it.
      const params = {};

      if (toAccountId) {
        params.toAccountId = toAccountId;
      }

      // Sent only when asked for. The endpoint leaves closed notes behind by
      // default, so omitting it keeps the conservative preview.
      if (includeClosedNotes) {
        params.includeClosedNotes = true;
      }

      return HttpService.getV2(
        `/accounts/${accountId}/notes/bulk-transfers/preview`,
        Object.keys(params).length ? params : null,
      )
        .then(response => response)
        .catch(error => Promise.reject(error));
    },
    createBulkNoteTransfer({ commit }, { accountId, payload }) {
      return HttpService.postV2(`/accounts/${accountId}/notes/bulk-transfers`, payload)
        .then(response => response)
        .catch(error => Promise.reject(error));
    },
    getBulkNoteTransfer({ commit }, { accountId, batchId }) {
      return HttpService.getV2(`/accounts/${accountId}/notes/bulk-transfers/${batchId}`)
        .then(response => response)
        .catch(error => Promise.reject(error));
    },
    approveBulkNoteTransfer({ commit }, { accountId, batchId, payload }) {
      return HttpService.postV2(
        `/accounts/${accountId}/notes/bulk-transfers/${batchId}/approval`,
        payload,
      )
        .then(response => response)
        .catch(error => Promise.reject(error));
    },
    cancelBulkNoteTransfer({ commit }, { accountId, batchId, payload }) {
      return HttpService.deleteV2(
        `/accounts/${accountId}/notes/bulk-transfers/${batchId}`,
        payload,
      )
        .then(response => response)
        .catch(error => Promise.reject(error));
    },
    getNote({ commit }, noteId) {
      return HttpService.getV2(`/notes/${noteId}`)
        .then(response => response)
        .catch((err) => {
          throw err;
        });
    },
    resetInterestAccrual({ commit }, noteId) {
      return HttpService.putV2(`/notes/${noteId}/interest-accrual`)
        .then(response => response)
        .catch((err) => {
          throw err;
        });
    },
    getNotes({ commit }, { accountId }) {
      commit(GET_NOTES_LIST_REQUEST);
      return HttpService.get(`notes/account/${accountId}`)
        .then((response) => {
          commit(GET_NOTES_LIST_SUCCESS);
          return response;
        })
        .catch(() => {
          commit(GET_NOTES_LIST_FAILURE);
        });
    },
    purchaseNote({ commit }, { data }) {
      commit(PURCHASE_NOTE_REQUEST);
      return HttpService.postV2('notes', data)
        .then(() => {
          commit(PURCHASE_NOTE_SUCCESS);
          return true;
        })
        .catch((error) => {
          commit(PURCHASE_NOTE_FAILURE);
          return Promise.reject(error);
        });
    },
    adjustInterestForNote({ commit }, { noteId, adjustmentObject }) {
      return HttpService.postV2(`notes/${noteId}/interest`, adjustmentObject)
        .then(() => true)
        .catch(error => Promise.reject(error));
    },
    getAccountNoteTransfer({ commit }, { accountId, noteId }) {
      try {
        return HttpService.getV2(`accounts/${accountId}/notes/${noteId}/transfer`);
      } catch (err) {
        throw err;
      }
    },
    async getAdditionalDepositInformation({ commit }, noteId) {
      try {
        const response = await HttpService.getV2(`/notes/${noteId}/funds`);
        return response;
      } catch (err) {
        throw err;
      }
    },
    async requestAddFunds({ commit }, { noteId, request }) {
      try {
        await HttpService.postV2(`/notes/${noteId}/funds`, request);
        return true;
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async approveAddFunds({ commit }, { noteId, request }) {
      try {
        await HttpService.putV2(`/notes/${noteId}/funds`, request);
        return true;
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async cancelNoteAddFunds({ commit }, { noteId, request }) {
      try {
        await HttpService.deleteV2(
          `/notes/${noteId}/funds`,
          request,
        );
        return true;
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async createAccountNoteTransfer({ commit }, { noteId, accountId, payload }) {
      try {
        await HttpService.postV2(`accounts/${accountId}/notes/${noteId}/transfer`, payload);
        return true;
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async updateAccountNoteTransfer({ commit }, { accountId, noteId, payload }) {
      try {
        await HttpService.putV2(
          `accounts/${accountId}/notes/${noteId}/transfer`,
          payload,
        );
        return true;
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async deleteAccountNoteTransfer({ commit }, { accountId, noteId }) {
      try {
        await HttpService.deleteV2(
          `accounts/${accountId}/notes/${noteId}/transfer`);
        return true;
      } catch (error) {
        return Promise.reject(error);
      }
    },
  },
};



// WEBPACK FOOTER //
// ./src/store/modules/note.js